import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { MeasurementGroupLoaderResponse, UpdateCapacityRequest } from '../types';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetTitle,
	SheetTrigger
} from '@/components/ui/sheet';
import { TrendingUpDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsDesktopSize } from '@/hooks/useIsDesktopSize';
import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import Spinner from '@/components/spinner/Spinner';
import { useMutation } from '@tanstack/react-query';
import { updateMinMaxCapacity } from '../constants';
import { toast } from 'sonner';
import { getUIErrorMessage } from '@/utils/errors';
import { UpdateMinMaxCapacityFormSchema, updateMinMaxCapacitySchema } from './min-max-capacity-zod-schema';
import { Input } from '@/components/ui/input';

type MinMaxCapacityUpdateFormProps = {
	isOpen: boolean;
	handleToggleForm: () => void;
};

const MinMaxCapacityUpdateForm = (props: MinMaxCapacityUpdateFormProps) => {
	const { isOpen, handleToggleForm } = props;
	const {
		measurementGroup: { data: measurementGroupData }
	} = useLoaderData() as MeasurementGroupLoaderResponse;

	const { isDesktop } = useIsDesktopSize();

	const revalidator = useRevalidator();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [error, setError] = useState('');

	const capacityUpdateForm = useForm<UpdateMinMaxCapacityFormSchema>({
		resolver: zodResolver(updateMinMaxCapacitySchema),
		defaultValues: {
			min: measurementGroupData.graphCapacity.min.toString(),
			max: measurementGroupData.graphCapacity.max.toString()
		}
	});

	const { isPending: isUpdating, mutate: mutateUpdateCapacity } = useMutation({
		mutationFn: (payload: UpdateCapacityRequest) => updateMinMaxCapacity(measurementGroupData.id, payload),
		mutationKey: ['measurement-groups', measurementGroupData.id],
		onSuccess: () => {
			capacityUpdateForm.reset();
			handleCloseForm();
			toast('Measurement group capacity updated successfully');
			revalidator.revalidate();
		},
		onError: error => {
			setError(getUIErrorMessage(error));
		}
	});

	const handleUpdateCapacity = () => {
		const submitForm = capacityUpdateForm.handleSubmit(formValues => {
			const payload: { min: number; max: number } = {
				min: parseFloat(formValues.min),
				max: parseFloat(formValues.max)
			};
			mutateUpdateCapacity(payload);
		});
		submitForm();
	};

	const handleCloseForm = () => {
		capacityUpdateForm.reset({
			min: measurementGroupData.graphCapacity.min.toString(),
			max: measurementGroupData.graphCapacity.max.toString()
		});
		handleToggleForm();
		setShowConfirmation(false);
		setError('');
	};

	return (
		<Sheet open={isOpen} onOpenChange={handleCloseForm}>
			<SheetTrigger>
				<Button
					asChild
					type="button"
					size="sm"
					variant="outline"
					onClick={handleToggleForm}
					className="flex justify-start w-fit text-xs"
				>
					<span>
						<TrendingUpDownIcon />
						Update capacity
					</span>
				</Button>
			</SheetTrigger>
			<SheetContent
				side={!isDesktop ? 'bottom' : 'right'}
				className={cn(' p-5 overflow-auto md:max-w-xl md:w-xl md:h-full w-full h-5/6')}
			>
				<SheetHeader>
					<SheetTitle>Update measurement group capacity</SheetTitle>
					<SheetDescription>Fill in the details. Click save when you're done.</SheetDescription>
				</SheetHeader>

				<Form {...capacityUpdateForm}>
					<form className="p-4 flex flex-col gap-3">
						<FormField
							name="min"
							control={capacityUpdateForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Min. Graph Capacity</FormLabel>
									<FormControl>
										<Input
											type="text"
											placeholder="Min. Graph Capacity"
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											value={field.value ?? ''}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							name="max"
							control={capacityUpdateForm.control}
							render={({ field }) => (
								<FormItem>
									<FormLabel>Max Graph Capacity</FormLabel>
									<FormControl>
										<Input
											type="text"
											placeholder="Max Graph Capacity"
											onChange={field.onChange}
											onBlur={field.onBlur}
											name={field.name}
											value={field.value ?? ''}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</form>
				</Form>

				{error && (
					<div className="text-red-500 text-sm text-center">
						<p>{error}</p>
					</div>
				)}

				<SheetFooter>
					{!showConfirmation ? (
						<Button type="submit" onClick={() => setShowConfirmation(true)}>
							Save changes
						</Button>
					) : (
						<div className="flex flex-col gap-2 text-sm justify-center items-center">
							<p>Are you sure you want to save the changes?</p>
							<div className="flex gap-2 w-full justify-center">
								<Button
									className="flex-1"
									variant="secondary"
									onClick={() => {
										setShowConfirmation(false);
										setError('');
									}}
									disabled={isUpdating}
								>
									Cancel
								</Button>
								<Button
									className="flex-1"
									type="submit"
									onClick={handleUpdateCapacity}
									disabled={isUpdating}
								>
									{error ? 'Try again ' : 'Save'}
									{isUpdating && <Spinner className="ml-2" />}
								</Button>
							</div>
						</div>
					)}
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
};

export default MinMaxCapacityUpdateForm;
